import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import "./AdminAnnouncementsPage.css";
import AdminHeaderLinks from "../../components/adminHeaderLinks/AdminHeaderLinks.jsx";
import {
  requestCreateAnnouncement,
  requestGetAllAnnouncements,
  requestGetAnnouncement,
  requestUpdateAnnouncement,
} from "../../services/wisdomServices/announcementService.js";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { getSessionItem } from "../../utils/helper/session.js";
import { InputTextarea } from "primereact/inputtextarea";
import { Can } from "../../security/Can.jsx";
import { PERMISSIONS } from "../../security/permissions.generated.js";

function AdminAnnouncementsPage() {
    const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
    const [announcements, setAnnouncements] = useState([]);
    const [announcementText, setAnnouncementText] = useState("");

    const [loadingCurrent, setLoadingCurrent] = useState(false);
    const [loadingList, setLoadingList] = useState(false);
    const [savingUpdate, setSavingUpdate] = useState(false);
    const [savingCreate, setSavingCreate] = useState(false);

    const user = getSessionItem("User");

    useEffect(() => {
        getCurrentAnnouncement();
        getAllAnnouncements();
    }, []);

    const getAllAnnouncements = async () => {
        try {
        setLoadingList(true);

        const response = await requestGetAllAnnouncements();

        const data = response?.data || response || [];

        setAnnouncements(Array.isArray(data) ? data : []);
        } catch (error) {
        console.error("Error loading announcements:", error);
        } finally {
        setLoadingList(false);
        }
    };

    const getCurrentAnnouncement = async () => {
        try {
        setLoadingCurrent(true);

        const response = await requestGetAnnouncement();

        const data = response?.data || response;

        setCurrentAnnouncement(data);
        setAnnouncementText(data?.announcement || "");
        } catch (error) {
        console.error("Error loading current announcement:", error);
        } finally {
        setLoadingCurrent(false);
        }
    };

    const updateAnnouncement = async () => {
        if (!currentAnnouncement?.id) return;

        try {
        setSavingUpdate(true);

        const payload = {
            announcement: announcementText,
        };

        const response = await requestUpdateAnnouncement(currentAnnouncement.id, payload);

        const updatedAnnouncement = response?.data || {
            ...currentAnnouncement,
            ...payload,
        };

        setCurrentAnnouncement(updatedAnnouncement);
        setAnnouncementText(updatedAnnouncement.announcement);

        sessionStorage.setItem("announcement", updatedAnnouncement.announcement);

        setAnnouncements((prev) =>
            prev.map((item) =>
            item.id === updatedAnnouncement.id ? updatedAnnouncement : item
            )
        );
        } catch (error) {
        console.error("Error updating announcement:", error);
        } finally {
        setSavingUpdate(false);
        }
    };

    const createAnnouncement = async () => {
        try {
        setSavingCreate(true);

        const payload = {
            announcement: announcementText,
            createdBy: user?.username,
        };

        const response = await requestCreateAnnouncement(payload);

        const newAnnouncement = response?.data || response;

        setCurrentAnnouncement(newAnnouncement);
        setAnnouncementText(newAnnouncement.announcement);

        sessionStorage.setItem("announcement", newAnnouncement.announcement);

        await getAllAnnouncements();
        } catch (error) {
        console.error("Error creating announcement:", error);
        } finally {
        setSavingCreate(false);
        }
    };

    const cancel = () => {
        setAnnouncementText(currentAnnouncement?.announcement || "");
    };

    const isCurrentAnnouncement = (rowData) => {
        return rowData.id === currentAnnouncement?.id;
    };

    const statusTemplate = (rowData) => {
        return isCurrentAnnouncement(rowData) ? (
        <Tag value="Current" severity="success" />
        ) : (
        <Tag value="Previous" severity="secondary" />
        );
    };

    const dateTemplate = (rowData) => {
        if (!rowData.createdAt) return "-";
        return new Date(rowData.createdAt).toLocaleDateString("pt-BR");;
    };

    const createdByTemplate = (rowData) => {
        if (!rowData.createdBy) return "-";

        return rowData.createdBy.split("@")[0];
    };

    const hasChanges = announcementText.trim() !== (currentAnnouncement?.announcement || "").trim();

    return (
        <div className="settingsContainer">
        <AdminHeaderLinks />

        <Box sx={{ flexGrow: 1 }} className="settingsBody">
            <Grid container spacing={2}>
                <Grid size={10}>
                    <h3 className="settingsLabel">Announcements</h3>
                </Grid>
                <Grid item size={5}>
                    <div className="current-announcement-card">
                        <div className="current-announcement-header">
                            <h4>Announcement</h4>
                            <Tag value="Current" severity="success" />
                        </div>

                        <div className="announcement-form">
                            <label htmlFor="announcement-txt" className="announcement-label">
                                Current Announcement
                                <span className="announcement-limit">
                                    (max 500 characters)
                                </span>
                            </label>

                            <InputTextarea
                            required
                            id="announcement-txt"
                            autoResize
                            rows={5}
                            maxLength={500}
                            value={announcementText}
                            onChange={(e) => setAnnouncementText(e.target.value)}
                            disabled={loadingCurrent}
                            />

                            <div className="announcements-btns">
                                <Can permission={PERMISSIONS.announcementsById_put}>
                                    <Button
                                        label="Update Current"
                                        severity="success"
                                        disabled={!announcementText.trim() || !hasChanges || savingUpdate}
                                        loading={savingUpdate}
                                        onClick={updateAnnouncement}
                                        icon="pi pi-sync"
                                    />
                                </Can>

                                <Can permission={PERMISSIONS.announcements_post}>
                                    <Button
                                        label="Create New Announcement"
                                        severity="info"
                                        disabled={!announcementText.trim() || !hasChanges || savingCreate}
                                        loading={savingCreate}
                                        onClick={createAnnouncement}
                                        icon="pi pi-check"
                                    />
                                </Can>

                                <Button
                                    label="Cancel"
                                    severity="warning"
                                    onClick={cancel}
                                    disabled={!hasChanges}
                                    icon="pi pi-times"
                                />
                            </div>
                        </div>
                    </div>
                </Grid>

                <Grid item size={10}>
                    <div className="announcements-list-card">
                    <h4>Previous Announcements</h4>

                    <DataTable
                        value={announcements}
                        loading={loadingList}
                        paginator
                        rows={5}
                        emptyMessage="No announcements found."
                        rowClassName={(rowData) =>
                        isCurrentAnnouncement(rowData) ? "current-announcement-row" : ""
                        }
                    >
                        <Column field="announcement" header="Announcement" />
                        <Column header="Created By" body={createdByTemplate}/>
                        <Column header="Created At" body={dateTemplate}/>
                        <Column header="Status" body={statusTemplate} />
                    </DataTable>
                    </div>
                </Grid>
            </Grid>
        </Box>
        </div>
    );
}

export default AdminAnnouncementsPage;